import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  DataGridBody,
  DataGridRow,
  DataGrid,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridCell,
  TableCellLayout,
  createTableColumn,
  Tooltip,
} from "@fluentui/react-components";
import { calculateStatus, formatDuration, formatDate, getPercentageColor, getStatusIcon } from '../../utils/syncStatusHelpers';

const tooltipStyles = `
  .fui-Tooltip,
  [class*="Tooltip"] {
    background-color: #323130 !important;
    color: white !important;
    padding: 8px 12px !important;
    border-radius: 4px !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2) !important;
  }
`;

function LastSyncDetailsTable({ syncDataList, tenantInt, customerId }) {
  const navigate = useNavigate();
  const location = useLocation();
  
  const getStatusDisplay = (syncData) => {
    if (!syncData.RunEnded) {
      return { status: 'In Progress', color: '#0078d4' };
    }
    
    const { status, color } = calculateStatus(
      syncData.SuccessfulTables || 0,
      syncData.TotalTables || 0
    );
    return { status, color };
  };

  const columns = [
    createTableColumn({
      columnId: 'InstanceId',
      compare: (a, b) => (a.InstanceId || '').localeCompare(b.InstanceId || ''),
      renderHeaderCell: () => 'Instance ID',
      renderCell: (item) => (
        <TableCellLayout>{item.InstanceId || 'N/A'}</TableCellLayout>
      )
    }),
    createTableColumn({
      columnId: 'StartedBy',
      compare: (a, b) => (a.StartedBy || '').localeCompare(b.StartedBy || ''),
      renderHeaderCell: () => 'Started By',
      renderCell: (item) => (
        <TableCellLayout>{item.StartedBy || 'N/A'}</TableCellLayout>
      )
    }),
    createTableColumn({
      columnId: 'Status',
      compare: (a, b) => {
        const statusA = getStatusDisplay(a).status;
        const statusB = getStatusDisplay(b).status;
        return statusA.localeCompare(statusB);
      },
      renderHeaderCell: () => 'Status',
      renderCell: (item) => {
        const statusDisplay = getStatusDisplay(item);
        const successfulTables = item.SuccessfulTables || 0;
        const totalTables = item.TotalTables || 0;
        const isInProgress = !item.RunEnded;
        const percentage = isInProgress ? null : (totalTables > 0 ? Math.round((successfulTables / totalTables) * 100) : null);
        const percentageColor = getPercentageColor(percentage);
        const statusIcon = getStatusIcon(statusDisplay.status, isInProgress);
        
        return (
          <TableCellLayout className="status-column-cell">
            <Tooltip
              relationship="description"
              content={
                <div style={{
                  backgroundColor: '#323130',
                  color: 'white',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  fontSize: '14px',
                  maxWidth: '300px'
                }}>
                  {isInProgress ? 'Sync in progress' : `${successfulTables} / ${totalTables}`}
                </div>
              }
            >
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'default' }}>
                <span>{statusIcon}</span>
                {percentage !== null && (
                  <span style={{ color: percentageColor, fontWeight: '500', fontSize: '14px' }}>
                    {percentage}%
                  </span>
                )}
              </div>
            </Tooltip>
          </TableCellLayout>
        );
      }
    }),
    createTableColumn({
      columnId: 'RunStarted',
      compare: (a, b) => new Date(a.RunStarted || 0) - new Date(b.RunStarted || 0),
      renderHeaderCell: () => 'Run Started',
      renderCell: (item) => (
        <TableCellLayout>{formatDate(item.RunStarted)}</TableCellLayout>
      )
    }),
    createTableColumn({
      columnId: 'RunEnded',
      compare: (a, b) => new Date(a.RunEnded || 0) - new Date(b.RunEnded || 0),
      renderHeaderCell: () => 'Run Ended',
      renderCell: (item) => (
        <TableCellLayout>{formatDate(item.RunEnded)}</TableCellLayout>
      )
    }),
    createTableColumn({
      columnId: 'Duration',
      compare: (a, b) => {
        const durationA = a.RunStarted && a.RunEnded ? new Date(a.RunEnded) - new Date(a.RunStarted) : 0;
        const durationB = b.RunStarted && b.RunEnded ? new Date(b.RunEnded) - new Date(b.RunStarted) : 0;
        return durationA - durationB;
      },
      renderHeaderCell: () => 'Duration',
      renderCell: (item) => (
        <TableCellLayout>{formatDuration(item.RunStarted, item.RunEnded)}</TableCellLayout>
      )
    }),
    createTableColumn({
      columnId: 'TotalTables',
      compare: (a, b) => (a.TotalTables || 0) - (b.TotalTables || 0),
      renderHeaderCell: () => 'Total Tables',
      renderCell: (item) => (
        <TableCellLayout>{item.TotalTables}</TableCellLayout>
      )
    }),
    createTableColumn({
      columnId: 'FailedTables',
      compare: (a, b) => {
        const failedA = (a.TotalTables || 0) - (a.SuccessfulTables || 0);
        const failedB = (b.TotalTables || 0) - (b.SuccessfulTables || 0);
        return failedA - failedB;
      },
      renderHeaderCell: () => 'Failed Tables',
      renderCell: (item) => (
        <TableCellLayout>{(item.TotalTables || 0) - (item.SuccessfulTables || 0)}</TableCellLayout>
      )
    }),
  ];

  return (
    <div>
      <style>{tooltipStyles}</style>
      <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px', marginTop: '0' }}>Last sync</h3>
      <div style={{ 
        border: '1px solid #e1dfdd', 
        borderRadius: '4px',
        overflow: 'hidden',
        marginTop: '20px'
      }}>
        <DataGrid
          items={syncDataList}
          columns={columns}
          sortable
          noNativeElements={false}
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            tableLayout: 'auto'
          }}
        >
          <DataGridHeader>
            <DataGridRow style={{ borderBottom: '2px solid #e1dfdd' }}>
              {({ renderHeaderCell }) => (
                <DataGridHeaderCell
                  style={{
                    borderRight: '1px solid #e1dfdd',
                    padding: '2px 4px',
                    backgroundColor: '#f3f2f1',
                    fontWeight: '600'
                  }}
                >
                  {renderHeaderCell()}
                </DataGridHeaderCell>
              )}
            </DataGridRow>
          </DataGridHeader>
          <DataGridBody>
            {({ item, rowId }) => (
              <DataGridRow
                key={rowId}
                style={{ borderBottom: '1px solid #e1dfdd', cursor: 'pointer' }}
                onClick={() => {
                  const searchParams = new URLSearchParams(location.search);
                  const tabParam = searchParams.get('tab');
                  const url = `/sync-instance/${tenantInt}/${item.InstanceId}/${customerId}${tabParam ? `?tab=${tabParam}` : ''}`;
                  navigate(url, { 
                    state: { 
                      startedBy: item.StartedBy,
                      startDate: item.RunStarted,
                      endDate: item.RunEnded
                    } 
                  });
                }}
              >
                {({ renderCell }) => (
                  <DataGridCell
                    style={{
                      borderRight: '1px solid #e1dfdd',
                      padding: '2px 4px'
                    }}
                  >
                    {renderCell(item)}
                  </DataGridCell>
                )}
              </DataGridRow>
            )}
          </DataGridBody>
        </DataGrid>
      </div>
    </div>
  );
}

export default LastSyncDetailsTable;
